// Dependencies
import * as React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';

// External Components
import { SyncLoader } from 'react-spinners';

// Material UI Components
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import { useTheme } from '@mui/material/styles';

// Hooks
import { useIsMounted } from 'usehooks-ts';

// Type
type Props = {
  children: React.ReactNode;
};
export default function ProgressBar({ children }: Props) {
  // =={ Redux }===============================================================
  const [loading, setLoading] = React.useState(false);

  // =={ Hooks }===============================================================
  const theme = useTheme();
  const router = useRouter();
  const isMounted = useIsMounted();

  NProgress.configure({ showSpinner: false });

  // =={ Effects }===============================================================
  React.useEffect(() => {
    const handleStart = () => {
      if (isMounted()) {
        setLoading(true);
        NProgress.start();
      }
    };
    const handleStop = () => {
      if (isMounted()) {
        setLoading(false);
        NProgress.done();
      }
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router, isMounted]);

  // =={ Render }===============================================================
  return (
    <>
      <GlobalStyles
        styles={{
          '#nprogress': {
            pointerEvents: 'none',
            '& .bar': {
              top: 0,
              left: 0,
              height: 2,
              width: '100%',
              position: 'fixed',
              zIndex: theme.zIndex.snackbar,
              backgroundColor: theme.palette.primary.main,
              boxShadow: `0 0 2px ${theme.palette.primary.main}`,
            },
            '& .peg': {
              right: 0,
              opacity: 1,
              width: 100,
              height: '100%',
              display: 'block',
              position: 'absolute',
              transform: 'rotate(3deg) translate(0px, -4px)',
              boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`,
            },
          },
        }}
      />

      {children}
      <Backdrop
        sx={{
          zIndex: 1600,
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
        }}
        open={loading}
      >
        <Box p={3}>
          <SyncLoader size={12} color={theme.palette.primary.main} />
        </Box>
      </Backdrop>
    </>
  );
}
